import { Breakpoint, SxProps, Theme } from "@mui/material";
import { forwardRef } from "react";
import SectionHeader, { SectionHeaderProps } from "@cc/section-header";
import { SetRequired } from "type-fest";
import { sxArrayUtil } from "@util/sx-helpers";
import { MotionStack, MotionStackProps } from "@cc/motion-components";
import { isObject } from "lodash";

export interface SectionWrapperProps extends MotionStackProps {
  SectionProps?: Omit<MotionStackProps, "children">;
  ContainerProps?: Omit<MotionStackProps, "children">;
  SectionHeaderProps?: Omit<SectionHeaderProps, "children">;
  disablePassColorToChildren?: boolean;
  showSectionBgTransactionProps?: SxProps<Theme> | true;
  navId?: string;
}

const SectionWrapper = forwardRef<
  HTMLElement | SVGElement,
  SectionWrapperProps
>((props, ref) => {
  const {
    children,
    navId,
    SectionProps = {},
    ContainerProps = {},
    SectionHeaderProps = {} as SetRequired<
      SectionWrapperProps,
      "SectionHeaderProps"
    >["SectionHeaderProps"],
    color,
    showSectionBgTransactionProps,
    disablePassColorToChildren,
    ...rest
  } = props;
  const { title } = SectionHeaderProps;
  const { sx, id, ...restProps } = SectionProps;

  return (
    <MotionStack
      nav-id={navId}
      id={navId}
      component={"section"}
      alignItems={"center"}
      height={1}
      overflow={"hidden"}
      color={color}
      sx={[
        {
          ...(!disablePassColorToChildren && {
            "*": {
              color: "inherit",
            },
          }),
        },
        ...sxArrayUtil(
          showSectionBgTransactionProps &&
            defaultSectionBgTransactionProps(
              isObject(showSectionBgTransactionProps)
                ? showSectionBgTransactionProps
                : {},
            ),
        ),
        ...sxArrayUtil(sx),
      ]}
      {...restProps}
    >
      <MotionStack
        px={basePx}
        py={basePy}
        width={1}
        maxWidth={"xxl"}
        height={"inherit"}
        color={color}
        {...ContainerProps}
        {...(!title && { ref, ...rest })}
      >
        {title ? (
          <>
            <SectionHeader {...SectionHeaderProps} />
            {children && (
              <MotionStack width={1} color={"inherit"} ref={ref} {...rest}>
                {children}
              </MotionStack>
            )}
          </>
        ) : (
          children
        )}
      </MotionStack>
    </MotionStack>
  );
});

export default SectionWrapper;

SectionWrapper.displayName = SectionWrapper.name;

export const defaultSectionBgTransactionProps =
  (css: SxProps<Theme> = {}): SxProps<Theme> =>
  (theme) => ({
    position: "relative",
    "&:before": {
      content: '""',
      position: "absolute",
      width: "204%",
      height: "auto",
      aspectRatio: 4.6,
      top: 0,
      right: 0,
      background: "url(/beyond.svg) space", //space
      pointerEvents: "none",
      backgroundSize: "contain",
      transform: "translate3d(0, 0, 0)",
      animation: "scroll 15s infinite linear",
      "@keyframes scroll": {
        "100%": {
          WebkitTransform: "translateX(50.5%)",
        },
      },
      ...(typeof css === "function" ? css(theme) : css),
    },
  });

export type BaseBreakpoint = Pick<
  {
    [keys in Breakpoint]: string | number;
  },
  "xs" | "md" | "xxl"
>;

export const basePx: BaseBreakpoint = {
  xs: 3,
  md: 5.5,
  xxl: 8,
};

export const basePy: BaseBreakpoint = {
  xs: 7,
  md: 9.5,
  xxl: 12,
};
